import React from 'react';
import PropTypes from 'prop-types';
import { Editor } from 'slate-react';
import Html from 'slate-html-serializer';
import FormatToolbar from './FormatToolbar';
import RULES from './utilities/rules';
import Bold from './icons/Bold';
import './RichTextEditor.css';
import Italicize from './icons/Italicize';
import Underline from './icons/Underline';
import Code from './icons/Code';

const html = new Html({ rules: RULES });

export default class RichTextEditor extends React.Component {
  constructor (props) {
    super(props);

    this.state = {
      activeTextFormats: [],
      value: html.deserialize(props.value || '<p></p>')
    };
  }

  ref = editor => {
    this.editor = editor;
  }

  hasMark = type => {
    const { value } = this.state;
    return value.activeMarks.some(mark => mark.type === type);
  }

  handleChange = ({ value }) => {
    this.setState({ value });

    const formatted = html.serialize(value);

    // bubble the html content back to parent
    this.props.onTextChange && this.props.onTextChange(formatted);
  }

  onFormatClick = (event, type) => {
    event.preventDefault();
    this.editor.toggleMark(type);
  }

  renderMark = (props, editor, next) => {
    const { mark, attributes } = props;

    switch (mark.type) {
      case 'bold':
        return <strong {...attributes}>{props.children}</strong>;
      case 'italic':
        return <em {...attributes}>{props.children}</em>;
      case 'underlined':
        return <u {...attributes}>{props.children}</u>;
      case 'code':
        return <code {...attributes}>{props.children}</code>;
      default:
        return next();
    }
  }

  renderBlock = (props, editor, next) => {
    const { attributes, children, node } = props;

    switch (node.type) {
      case 'quote':
        return <blockquote {...attributes}>{children}</blockquote>;
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>;
      case 'heading-one':
        return <h1 {...attributes}>{children}</h1>;
      case 'heading-two':
        return <h2 {...attributes}>{children}</h2>;
      case 'heading-three':
        return <h3 {...attributes}>{children}</h3>;
      case 'heading-four':
        return <h4 {...attributes}>{children}</h4>;
      case 'heading-five':
        return <h5 {...attributes}>{children}</h5>;
      case 'heading-six':
        return <h6 {...attributes}>{children}</h6>;
      case 'list-item':
        return <li {...attributes}>{children}</li>;
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>;
      default:
        return next();
    }
  }

  renderMarkButton = (type, icon) => {
    return (
      <button
        onClick={event => this.onFormatClick(event, type)}
        className='rte-tooltip-icon-button'
        style={{ opacity: this.hasMark(type) ? 1 : 0.25 }}
      >
        { icon }
      </button>
    );
  }

  render () {
    return (
      <div className='rte-container'>
        <FormatToolbar>
          { this.renderMarkButton('bold', <Bold />)}
          { this.renderMarkButton('italic', <Italicize />)}
          { this.renderMarkButton('underline', <Underline />)}
          { this.renderMarkButton('code', <Code />)}
        </FormatToolbar>
        <Editor
          ref={this.ref}
          value={this.state.value}
          onChange={this.handleChange}
          renderMark={this.renderMark}
          renderBlock={this.renderBlock}
          renderEditor={(props, editor, next) => {
            const children = next();

            return (
              <div style={{ margin: 8, fontFamily: 'proxima-soft, sans-seriff', fontSize: '14px' }}>
                { children }
              </div>
            );
          }}
        />
      </div>
    );
  }
}

RichTextEditor.propTypes = {
  value: PropTypes.string, // html string
  onChange: PropTypes.func // bubble changes up to parent component
};
